import type { LucideIcon } from "lucide-react";
import { Control, FieldValues, Path } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { cn } from "@/lib/utils";

// Input types that we support
type InputType = "text" | "number" | "email" | "password" | "textarea";

// Base props that all form fields share
export interface BaseFormFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  className?: string;
  description?: string;
  readonly?: boolean;
  hiddenLabel?: boolean;
  required?: boolean;
  disabled?: boolean;
  icon?: LucideIcon;
}

// Props specific to number inputs
interface NumberFieldProps<T extends FieldValues>
  extends BaseFormFieldProps<T> {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
}

// Props for text-based inputs
interface TextFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type?: Exclude<InputType, "number">;
  maxLength?: number;
}

// Union type of all possible form field props
type FormFieldWrapperProps<T extends FieldValues> =
  | NumberFieldProps<T>
  | TextFieldProps<T>;

export const FormFieldWrapper = <T extends FieldValues>({
  control,
  name,
  label,
  type = "text",
  className,
  description,
  readonly = false,
  hiddenLabel = false,
  required = false,
  disabled = false,
  icon: Icon,
  ...props
}: FormFieldWrapperProps<T>) => {
  const renderInput = (field: FieldValues) => {
    switch (type) {
      case "textarea":
        return (
          <Textarea
            {...field}
            id={name}
            placeholder={props.placeholder}
            className={className}
            maxLength={(props as TextFieldProps<T>).maxLength}
            readOnly={readonly}
            disabled={disabled}
          />
        );
      case "number":
        const numberProps = props as NumberFieldProps<T>;
        return (
          <Input
            {...field}
            id={name}
            type="number"
            min={numberProps.min}
            max={numberProps.max}
            step={numberProps.step}
            className={cn(
              "transition-all duration-300",
              Icon && "pl-10",
              className
            )}
            onChange={(e) => field.onChange(Number(e.target.value))}
            readOnly={readonly}
            disabled={disabled}
          />
        );
      default:
        const textProps = props as TextFieldProps<T>;
        return (
          <Input
            {...field}
            id={name}
            type={type}
            placeholder={textProps.placeholder}
            className={cn(
              "transition-all duration-300",
              Icon && "pl-10",
              className
            )}
            maxLength={textProps.maxLength}
            readOnly={readonly}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && (
            <FormLabel htmlFor={name} className={cn(hiddenLabel && "sr-only")}>
              {label} {required && <span className="text-destructive">*</span>}
            </FormLabel>
          )}
          <div className="relative">
            {Icon && (
              <div className="absolute left-2 top-2 w-4 h-4 text-muted-foreground z-10">
                <Icon size={16} />
              </div>
            )}
            <FormControl>{renderInput(field)}</FormControl>
          </div>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
